import { useState, useMemo, useCallback, useEffect } from 'react'
import { FormikHelpers } from 'formik'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux/store'
import {
  resendInvite,
  sendInvite,
  removePlayer,
  bulkRemoveStudents,
  getRoomMembers,
} from '@/redux/asyncThunks/room'
import { GameRoomStatus } from '@/redux/types/room'
import { fetchTeamsByRoomId } from '@/redux/asyncThunks/team'
import { getCommunicationSettingsAPI } from '@/api/room'
import OGEDeletionModal from '../OGE/OGEDeletionModal'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { UserRole } from '@/constants/room'
import { cn } from '@/lib/utils'
import { InviteForm } from '@/components/InviteMembers/InviteMemberForm'
import { MemberList } from '@/components/InviteMembers/InvitedMemberList'
import { UploadRosterModal } from '@/components/InviteMembers/UploadRosterModal'
import { BulkInviteSection } from '@/components/InviteMembers/BulkInviteSection'

type ResendLoadingState = {
  [key: string]: boolean
}

interface InviteMemberModalProps {
  canInviteProfessors: boolean
  isOpen: boolean
  onClose: () => void
}

type FormValues = {
  email: string
  role: string
  sendInvite: boolean
  requirePasswordConfiguration: boolean
  teamId: number | null
  newTeamName: string
}

export function InviteMemberModal({
  isOpen,
  onClose,
  canInviteProfessors,
}: InviteMemberModalProps) {
  const dispatch = useDispatch<AppDispatch>()
  const { currentRoom, roomMembers } = useSelector(
    (state: RootState) => state.room
  )
  const [resendLoading, setResendLoading] = useState<ResendLoadingState>({})
  const isLoading = useSelector((state: RootState) => state.room.isLoading)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false)
  const [playerToDelete, setPlayerToDelete] = useState<{
    id: string
    email: string
  } | null>(null)
  const [activeTab, setActiveTab] = useState(UserRole.Student)
  const [isUploadRosterModalOpen, setIsUploadRosterModalOpen] = useState(false)
  const [roomTemplate, setRoomTemplate] = useState<string | null>(null)

  const isGameStarted = useMemo(
    () =>
      currentRoom?.status === GameRoomStatus.IN_PROGRESS ||
      currentRoom?.status === GameRoomStatus.COMPLETED,
    [currentRoom?.status]
  )

  // Fetch teams when modal opens for students
  useEffect(() => {
    if (isOpen && currentRoom?.roomid && activeTab === UserRole.Student) {
      dispatch(fetchTeamsByRoomId(currentRoom.roomid))
    }
  }, [isOpen, currentRoom?.roomid, activeTab, dispatch])

  // Fetch room template when modal opens
  useEffect(() => {
    const fetchTemplate = async () => {
      if (isOpen && currentRoom?.roomid) {
        try {
          const data = await getCommunicationSettingsAPI(currentRoom.roomid)
          setRoomTemplate(data.invitation_message_template || null)
        } catch (error) {
          console.error('Failed to load room template:', error)
          setRoomTemplate(null)
        }
      }
    }
    fetchTemplate()
  }, [isOpen, currentRoom?.roomid])

  const handleInvite = useCallback(
    async (
      values: FormValues,
      { resetForm, setFieldError }: FormikHelpers<FormValues>
    ) => {
      const emailExists = roomMembers.some(
        (member) =>
          member.member_email.toLowerCase() === values.email.toLowerCase()
      )

      if (emailExists) {
        setFieldError('email', 'Player is already invited')
        return
      }

      try {
        const teamAssignments =
          activeTab === UserRole.Student && values.teamId
            ? { [values.email]: values.teamId }
            : undefined

        if (activeTab === UserRole.Student) {
          await dispatch(
            sendInvite({
              roomId: currentRoom?.roomid!,
              emails: [values.email],
              userRole: UserRole.Student,
              sendEmail: values.sendInvite,
              teamAssignments,
              requirePasswordConfiguration:
                values.requirePasswordConfiguration,
            })
          ).unwrap()
        } else {
          await dispatch(
            sendInvite({
              roomId: currentRoom?.roomid!,
              emails: [values.email],
              userRole: UserRole.Professor,
              sendEmail: values.sendInvite,
              requirePasswordConfiguration:
                values.requirePasswordConfiguration,
            })
          ).unwrap()
        }
        dispatch(getRoomMembers(currentRoom?.roomid!))
        resetForm()
      } catch (err) {
        setFieldError('email', err as string)
      }
    },
    [roomMembers, activeTab, currentRoom?.roomid, dispatch]
  )

  const handleResend = useCallback(
    async (memberEmail: string, customMessage?: string) => {
      const member = roomMembers.find((m) => m.member_email === memberEmail)
      if (member && currentRoom?.roomid) {
        setResendLoading((prev) => ({ ...prev, [memberEmail]: true }))
        try {
          await dispatch(
            resendInvite({
              roomId: currentRoom.roomid,
              emails: [member.member_email],
              userRole: activeTab,
              customMessage,
            })
          ).unwrap()
        } catch (err) {
          console.error('Failed to resend invite:', err)
        }
        setResendLoading((prev) => ({ ...prev, [memberEmail]: false }))
      }
    },
    [roomMembers, activeTab, currentRoom?.roomid, dispatch]
  )

  const inviteDraftMember = useCallback(
    async (memberEmail: string) => {
      const member = roomMembers.find((m) => m.member_email === memberEmail)
      if (member && currentRoom?.roomid) {
        setResendLoading((prev) => ({ ...prev, [memberEmail]: true }))
        try {
          if (activeTab === UserRole.Professor) {
            await dispatch(
              sendInvite({
                roomId: currentRoom.roomid,
                emails: [member.member_email],
                userRole: activeTab,
                sendEmail: true,
              })
            ).unwrap()
          } else {
            await dispatch(
              sendInvite({
                roomId: currentRoom.roomid,
                emails: [member.member_email],
                userRole: activeTab,
                sendEmail: true,
              })
            ).unwrap()
          }
          dispatch(getRoomMembers(currentRoom?.roomid!))
        } catch (err) {
          console.error('Failed to send invite:', err)
        }
        setResendLoading((prev) => ({ ...prev, [memberEmail]: false }))
      }
    },
    [roomMembers, activeTab, currentRoom?.roomid, dispatch]
  )

  const handleDeleteClick = useCallback(
    (playerId: string, playerEmail: string) => {
      setPlayerToDelete({ id: playerId, email: playerEmail })
      setIsDeleteModalOpen(true)
    },
    []
  )

  const handleConfirmDelete = useCallback(async () => {
    if (playerToDelete && currentRoom?.roomid) {
      try {
        await dispatch(
          removePlayer({
            roomId: currentRoom.roomid,
            playerId: playerToDelete.id,
          })
        ).unwrap()
        dispatch(getRoomMembers(currentRoom.roomid))
      } catch (err) {
        console.error('Failed to remove player:', err)
      }
    }
  }, [playerToDelete, currentRoom?.roomid, dispatch])

  const handleBulkDeleteClick = useCallback(() => {
    setIsBulkDeleteModalOpen(true)
  }, [])

  const handleConfirmBulkDelete = useCallback(async () => {
    if (currentRoom?.roomid) {
      try {
        await dispatch(bulkRemoveStudents(currentRoom.roomid)).unwrap()
        dispatch(getRoomMembers(currentRoom.roomid))
      } catch (err) {
        console.error('Failed to remove all students:', err)
      }
    }
  }, [currentRoom?.roomid, dispatch])

  const studentCount = useMemo(() => {
    return roomMembers.filter((member) => member.role === 'player').length
  }, [roomMembers])

  const studentConfig = useMemo(
    () => ({
      buttonText: 'Add Student',
      disabledTooltipText:
        'New players cannot be invited once the game has started.',
      title: 'Invited Students',
      roleOptions: [],
      showTeamInfo: true,
    }),
    []
  )

  const professorConfig = useMemo(
    () => ({
      buttonText: 'Add Professor',
      disabledTooltipText:
        'New professors cannot be invited once the game has started.',
      title: 'Invited Professors',
      roleOptions: [{ value: 'Co-professor', label: 'Co-professor' }],
      showTeamInfo: false,
    }),
    []
  )

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[27.4rem] pt-12 pb-11'>
        <DialogHeader>
          <DialogTitle className='text-base font-bold text-[#0F172A]'>
            Share Room ID
          </DialogTitle>
          <DialogDescription className='m-0 text-sm text-[#64748B]'>
            Invite members to join this game
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as UserRole)}
          className='w-full'
        >
          <TabsList
            className={cn(
              'grid w-full',
              canInviteProfessors ? 'grid-cols-2' : 'grid-cols-1'
            )}
          >
            <TabsTrigger value={UserRole.Student}>Students</TabsTrigger>
            {canInviteProfessors && (
              <TabsTrigger value={UserRole.Professor}>Professors</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value={UserRole.Student} className='space-y-6 mt-6'>
            <div className='max-w-full space-y-6'>
              <BulkInviteSection
                isGameStarted={isGameStarted}
                onUploadClick={() => setIsUploadRosterModalOpen(true)}
                disabledTooltipText={studentConfig.disabledTooltipText}
              />

              <InviteForm
                userRole={UserRole.Student}
                isGameStarted={isGameStarted}
                roomId={currentRoom?.roomid || ''}
                onSubmit={handleInvite}
                roleOptions={studentConfig.roleOptions}
                buttonText={studentConfig.buttonText}
                disabledTooltipText={studentConfig.disabledTooltipText}
              />

              <MemberList
                members={roomMembers}
                userRole={UserRole.Student}
                title={studentConfig.title}
                isGameStarted={isGameStarted}
                isLoading={isLoading}
                resendLoading={resendLoading}
                onResend={handleResend}
                onSendInvite={inviteDraftMember}
                onDelete={handleDeleteClick}
                showTeamInfo={studentConfig.showTeamInfo}
                roomTemplate={roomTemplate}
                headerAction={
                  studentCount > 0 && !isGameStarted ? (
                    <Button
                      variant='destructive'
                      size='sm'
                      onClick={handleBulkDeleteClick}
                      className='bg-red-600 hover:bg-red-700 text-xs px-2 py-1 h-7'
                    >
                      Delete All
                    </Button>
                  ) : undefined
                }
              />
            </div>
          </TabsContent>

          {canInviteProfessors && (
            <TabsContent value={UserRole.Professor} className='space-y-6 mt-6'>
              <div className='max-w-full space-y-6'>
                <InviteForm
                  userRole={UserRole.Professor}
                  isGameStarted={isGameStarted}
                  roomId={currentRoom?.roomid || ''}
                  onSubmit={handleInvite}
                  roleOptions={professorConfig.roleOptions}
                  buttonText={professorConfig.buttonText}
                  disabledTooltipText={professorConfig.disabledTooltipText}
                />

                <MemberList
                  members={roomMembers}
                  userRole={UserRole.Professor}
                  title={professorConfig.title}
                  isGameStarted={isGameStarted}
                  isLoading={isLoading}
                  resendLoading={resendLoading}
                  onResend={handleResend}
                  onSendInvite={inviteDraftMember}
                  onDelete={handleDeleteClick}
                  showTeamInfo={professorConfig.showTeamInfo}
                  roomTemplate={roomTemplate}
                />
              </div>
            </TabsContent>
          )}
        </Tabs>

        <OGEDeletionModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title='Remove Player'
          description={`Are you sure you want to remove ${playerToDelete?.email} from this game room?`}
          confirmButtonText='Remove'
          cancelButtonText='Cancel'
        />

        <OGEDeletionModal
          isOpen={isBulkDeleteModalOpen}
          onClose={() => setIsBulkDeleteModalOpen(false)}
          onConfirm={handleConfirmBulkDelete}
          title='Delete All Students'
          description={`Are you sure you want to remove all ${studentCount} student(s) from this game room? This action cannot be undone.`}
          confirmButtonText='Delete All'
          cancelButtonText='Cancel'
        />

        <UploadRosterModal
          isOpen={isUploadRosterModalOpen}
          onClose={() => setIsUploadRosterModalOpen(false)}
          roomId={currentRoom?.roomid || ''}
          isGameStarted={isGameStarted}
        />
      </DialogContent>
    </Dialog>
  )
}
